import { ChangeDetectionStrategy, Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { GatilhoService } from '../../../core/gatilho/gatilho.service';
import { GatilhoEmissao } from '../../../core/gatilho/gatilho.model';

const EVENTO_LABELS: Record<string, string> = {
  INDICACAO_PF:    'Geral',
  ASSINATURA_B2C:  'Assinatura B2C',
  COLABORADOR_B2B: 'Colaborador B2B',
  EMISSAO_MANUAL:  'Emissão manual',
};

const TIPO_CLIENTE_LABELS: Record<string, string> = {
  P: 'Particular',
  A: 'Assinante (B2C)',
  E: 'Empresa (B2B)',
};

@Component({
  selector: 'app-gatilho-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="page">
      <header class="page__top">
        <div class="page__heading">
          <h1 class="page__title">Gatilhos de emissao</h1>
          <p class="page__sub">Defina quando cada cupom deve ser emitido automaticamente</p>
        </div>
        <div class="page__actions">
          <button class="btn" (click)="carregar()" [disabled]="loading()">Recarregar</button>
          <a class="btn btn--primary" routerLink="/gatilhos/novo">+ Novo gatilho</a>
        </div>
      </header>

      <div class="filtros">
        <button class="chip" [class.chip--on]="filtroSit() === ''"  (click)="filtroSit.set('')">Todos</button>
        <button class="chip" [class.chip--on]="filtroSit() === 'A'" (click)="filtroSit.set('A')">Ativos</button>
        <button class="chip" [class.chip--on]="filtroSit() === 'I'" (click)="filtroSit.set('I')">Inativos</button>
        <select class="select-tipo" [ngModel]="filtroEvento()" (ngModelChange)="filtroEvento.set($event)" aria-label="Filtrar por evento">
          <option value="">Todos os eventos</option>
          <option value="INDICACAO_PF">Geral</option>
          <option value="ASSINATURA_B2C">Assinatura B2C</option>
          <option value="COLABORADOR_B2B">Colaborador B2B</option>
          <option value="EMISSAO_MANUAL">Emissão manual</option>
        </select>
        @if (visiveis().length > 0) {
          <span class="count">{{ visiveis().length }} resultado(s)</span>
        }
      </div>

      @if (loading()) {
        <div class="state">Carregando…</div>
      } @else if (erro()) {
        <div class="alert alert--erro">{{ erro() }}</div>
      } @else if (visiveis().length === 0) {
        <div class="state">Nenhum gatilho cadastrado.</div>
      } @else {
        <table class="tbl">
          <thead>
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>Evento</th>
              <th>Cupom</th>
              <th>Vigencia</th>
              <th>Liberacao</th>
              <th>Status</th>
              <th class="acoes"></th>
            </tr>
          </thead>
          <tbody>
            @for (g of visiveis(); track g.nrSequencia) {
              <tr [class.row--inativo]="g.ieSituacao === 'I'">
                <td class="num">{{ g.nrSequencia }}</td>
                <td>
                  <a class="nome" [routerLink]="['/gatilhos', g.nrSequencia, 'editar']">
                    {{ g.dsNome ?? 'Gatilho #' + g.nrSequencia }}
                  </a>
                </td>
                <td><span class="tag tag--evento">{{ eventoLabel(g.ieEvento) }}</span></td>
                <td>
                  @if (g.ieCupomDinamico === 'S') {
                    <span class="pill pill--dyn">dinamico</span>
                  } @else {
                    <span class="pill">#{{ g.nrSeqCupom }}</span>
                  }
                </td>
                <td class="dias">{{ g.nrDiasVigencia }} <span class="dias__un">dias</span></td>
                <td class="dias">{{ g.nrDiasLiberacao }} <span class="dias__un">dias</span></td>
                <td>
                  <span class="status" [class.status--inativo]="g.ieSituacao === 'I'">
                    {{ g.ieSituacao === 'A' ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="acoes">
                  <a class="icon-btn" [routerLink]="['/gatilhos', g.nrSequencia, 'editar']" title="Editar">✎</a>
                  @if (g.ieSituacao === 'A') {
                    <button class="icon-btn icon-btn--danger" (click)="inativar(g)" title="Inativar">x</button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .page { padding: 32px 40px; max-width: 1200px; margin: 0 auto; font-family: var(--font-sans); color: rgb(var(--color-ink)); }
    .page__top { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 1px solid rgb(var(--color-line)); margin-bottom: 20px; gap: 16px; }
    .page__heading { display: flex; flex-direction: column; gap: 2px; }
    .page__title { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.02em; }
    .page__sub { margin: 0; color: rgb(var(--color-ink-3)); font-size: 13px; font-weight: 400; }
    .page__actions { display: flex; gap: 8px; flex-shrink: 0; padding-top: 4px; }

    .btn { height: 34px; padding: 0 14px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); cursor: pointer; font-size: 13px; font-weight: 500; font-family: inherit; text-decoration: none; display: inline-flex; align-items: center; transition: background-color 120ms, border-color 120ms, color 120ms; }
    .btn:hover:not(:disabled) { background: rgb(var(--color-hover)); }
    .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn--primary { background: rgb(var(--color-accent)); color: #fff; border-color: rgb(var(--color-accent)); font-weight: 600; }
    .btn--primary:hover:not(:disabled) { background: rgb(var(--color-accent-2)); }

    .filtros { display: flex; gap: 8px; margin-bottom: 16px; align-items: center; }
    .chip { padding: 6px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: 999px; background: rgb(var(--color-paper)); font-size: 12px; cursor: pointer; color: rgb(var(--color-ink-2)); }
    .chip--on { background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); border-color: rgb(var(--color-accent)); font-weight: 600; }
    .select-tipo { padding: 6px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); background: rgb(var(--color-paper)); font-size: 12px; cursor: pointer; color: rgb(var(--color-ink-2)); font-family: inherit; outline: none; }
    .select-tipo:focus { border-color: rgb(var(--color-accent)); }
    .count { font-size: 12px; color: rgb(var(--color-ink-3)); margin-left: auto; }

    .state { padding: 28px; background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); color: rgb(var(--color-ink-3)); text-align: center; }
    .alert--erro { background: rgb(var(--color-danger-bg)); border: 1px solid rgb(var(--color-danger)); color: rgb(var(--color-danger)); padding: 12px 16px; border-radius: var(--radius); }

    .tbl { width: 100%; border-collapse: collapse; background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); overflow: hidden; font-size: 13px; }
    .tbl th { text-align: left; padding: 12px 20px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: rgb(var(--color-ink-3)); background: rgb(var(--color-bg-soft)); border-bottom: 1px solid rgb(var(--color-line)); }
    .tbl td { padding: 12px 20px; border-bottom: 1px solid rgb(var(--color-line)); vertical-align: middle; }
    .tbl tbody tr:last-child td { border-bottom: none; }
    .tbl .num { font-variant-numeric: tabular-nums; }
    .dias { font-variant-numeric: tabular-nums; font-weight: 600; font-size: 13px; }
    .dias__un { font-weight: 400; color: rgb(var(--color-ink-3)); font-size: 11px; }
    .tbl .acoes { text-align: right; white-space: nowrap; }
    .row--inativo td { color: rgb(var(--color-ink-3)); background: rgb(var(--color-bg-soft)); }

    .nome { font-weight: 600; color: rgb(var(--color-accent-2)); text-decoration: none; }
    .nome:hover { text-decoration: underline; }

    .tag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
    .tag--evento { background: rgb(var(--color-bg-soft)); color: rgb(var(--color-ink-2)); }

    .pill { display: inline-block; padding: 2px 8px; background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); border-radius: 4px; font-family: var(--font-mono); font-size: 12px; font-weight: 600; }
    .pill--dyn { background: rgb(var(--color-warn-bg)); color: rgb(var(--color-warn)); }

    .status { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); text-transform: uppercase; letter-spacing: 0.04em; }
    .status--inativo { background: rgb(var(--color-hover)); color: rgb(var(--color-ink-3)); }

    .icon-btn { display: inline-block; padding: 4px 8px; border: 1px solid rgb(var(--color-line-2)); border-radius: 4px; background: rgb(var(--color-paper)); color: rgb(var(--color-ink-2)); cursor: pointer; font-size: 14px; text-decoration: none; }
    .icon-btn:hover { background: rgb(var(--color-hover)); }
    .icon-btn--danger:hover { background: rgb(var(--color-danger-bg)); color: rgb(var(--color-danger)); border-color: rgb(var(--color-danger)); }
  `],
})
export class GatilhoListComponent implements OnInit {
  private readonly svc = inject(GatilhoService);
  private readonly router = inject(Router);

  protected readonly itens = signal<GatilhoEmissao[]>([]);
  protected readonly loading = signal(false);
  protected readonly erro = signal<string | null>(null);
  protected readonly filtroSit = signal<'' | 'A' | 'I'>('A');
  protected readonly filtroEvento = signal<string>('');

  protected readonly visiveis = computed(() => {
    const sit = this.filtroSit();
    const ev = this.filtroEvento();
    return this.itens().filter(g =>
      (!sit || g.ieSituacao === sit) &&
      (!ev || g.ieEvento === ev));
  });

  ngOnInit(): void { this.carregar(); }

  carregar(): void {
    this.loading.set(true);
    this.erro.set(null);
    this.svc.listar().subscribe({
      next: res => { this.itens.set(res.data ?? []); this.loading.set(false); },
      error: e => { this.erro.set(e?.message ?? 'Falha ao listar gatilhos.'); this.loading.set(false); },
    });
  }

  inativar(g: GatilhoEmissao): void {
    if (!confirm(`Inativar gatilho #${g.nrSequencia}?`)) return;
    this.svc.inativar(g.nrSequencia).subscribe({
      next: () => this.carregar(),
      error: e => this.erro.set(e?.message ?? 'Falha ao inativar.'),
    });
  }

  eventoLabel(e: string): string { return EVENTO_LABELS[e] ?? e; }
  tipoClienteLabel(c?: string | null): string {
    if (!c) return 'Qualquer';
    return TIPO_CLIENTE_LABELS[c] ?? c;
  }
}
